import { IFormField } from "@faire/retailer-visitor-shared/components/SignUpForm/types";
import { handleKeyDown } from "@faire/retailer-visitor-shared/lib/handleKeyDown";
import { trackSignupFillEmailAddressOnPreSignUpFormClick } from "@faire/web-api--source/events/signup/click/fillEmailAddressOnPreSignUpForm";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Input } from "@faire/web/slate/Input";
import once from "lodash/once";
import React, { useEffect, useRef } from "react";

interface IProps {
  email: IFormField<string>;
  onContinue: () => void;
  onBlur: () => void;
  onFocus?: () => void;
  disabled?: boolean;
  markUserEntersEmail: () => void;
  omitLabel?: boolean;
}

export const EmailInput: React.FC<IProps> = ({
  email,
  onContinue,
  onBlur,
  onFocus,
  disabled,
  markUserEntersEmail,
  omitLabel,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const inputRef = useRef<HTMLInputElement>(null);
  const isMounted = useRef<boolean>(false);
  // Handle case where user has started to enter text during SSR by updating state
  // on hydration.
  useEffect(() => {
    if (isMounted.current) {
      return;
    }
    isMounted.current = true;
    const domValue = inputRef.current?.value;
    if (domValue) {
      email.selectValue(domValue);
    }
  }, [email]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    once(() => trackSignupFillEmailAddressOnPreSignUpFormClick());
    markUserEntersEmail();
    email.selectValue(e.currentTarget.value);
  };

  const inputLabel = strictLocalize({
    id: "rTiQu/",
    defaultMessage: "Business email",
    description: {
      text: "Label for Email address input field, we want to shorten the label to make it more concise",
      img: "https://cdn.faire.com/fastly/fdb5b1ba7ce3bffc6398560421087f9f73a6c722855c8d1789cfafea70fd786f.png",
    },
  });

  return (
    <Input
      ref={inputRef}
      disableFlex
      disabled={disabled}
      label={omitLabel ? undefined : inputLabel}
      name="email"
      autoComplete="email"
      value={email.value}
      error={email.error}
      onChange={onChange}
      onKeyDown={handleKeyDown(onContinue)}
      onBlur={onBlur}
      onFocus={onFocus}
      data-test-id="email"
    />
  );
};
